import React, { useState, useEffect } from "react";
import {
    Box,
    TextField,
    Button,
    Typography,
    Divider,
    List,
    ListItem,
    ListItemText,
    IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { db } from "../../firebase";
import { collection, addDoc, getDocs, deleteDoc, doc } from "firebase/firestore";
import AdsterraAd from "../../pages/Ads/AdsterraAd";
import InConstruction from "./InConstrution";
import NavBarAdm from "../../adm/navbarAdm";

export default function Add_Bus_Park() {
    const [value, setValue] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [docsList, setDocsList] = useState([]);
    const pattern = /^\d{3,4}$/; // only digits, 3 or 4 characters

    const fetchDocs = async () => {
        const q = await getDocs(collection(db, "BusParkPlaces"));
        const list = q.docs.map(d => ({ id: d.id, ...d.data(), place: String(d.data()?.place ?? "") }));

        // sort: numeric when possible (smaller -> bigger), otherwise fallback to locale compare
        list.sort((a, b) => {
            const na = Number(a.bus);
            const nb = Number(b.bus);
            const aIsNum = !Number.isNaN(na);
            const bIsNum = !Number.isNaN(nb);

            if (aIsNum && bIsNum) return na - nb;
            if (aIsNum && !bIsNum) return -1;
            if (!aIsNum && bIsNum) return 1;
            return String(a.bus).localeCompare(String(b.bus), undefined, { numeric: true });
        });

        setDocsList(list);
    };

    useEffect(() => {
        void fetchDocs();
    }, []);

    const handleAdd = async () => {
        const v = value.trim();
        if (!pattern.test(v)) {
            setError("Die Nummer darf nur Ziffern enthalten und muss 3 oder 4 Stellen haben.");
            return;
        }

        // prevent duplicate bus values in the collection
        const exists = docsList.some(d => String(d.bus) === String(v));
        if (exists) {
            setError("Diese Busnummer existiert bereits.");
            return;
        }

        setLoading(true);
        setError("");
        try {
            // store place as an empty string and status as a number (0) for new entries
            await addDoc(collection(db, "BusParkPlaces"), {
                bus: String(v),
                place: "",        // changed: place is a string now (unassigned)
                info: "Kurz info",
                status: 0         // ensure status is stored as a number
            });
            setValue("");
            await fetchDocs();
        } catch (err) {
            console.error("Failed to add bus:", err);
            setError("Fehler beim Hinzufügen. Siehe Konsole.");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await deleteDoc(doc(db, "BusParkPlaces", id));
            setDocsList(prev => prev.filter(d => d.id !== id));
        } catch (err) {
            console.error("Failed to delete doc:", err);
        }
    };

    return (
        <>
<NavBarAdm />

            {/* top banner */}
            <InConstruction />

            <Box sx={{ maxWidth: 420, mx: "auto", mt: 4, p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Busnummer hinzufügen
                </Typography>

                <TextField
                    label="Busnummer"
                    value={value}
                    onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, "");
                        setValue(digits);
                        if (error) setError("");
                    }}
                    fullWidth
                    inputProps={{ inputMode: "numeric", pattern: "\\d{3,4}" }}
                    helperText={error || "Enter 3 or 4 digits"}
                    error={Boolean(error)}
                    disabled={loading}
                    sx={{ mb: 2 }}
                />

                <Button variant="contained" onClick={handleAdd} disabled={loading} fullWidth>
                    Hinzufügen
                </Button>

                <Divider sx={{ my: 3 }} />

                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Vorhandene Busnummern
                </Typography>

                <List dense>
                    {docsList.length === 0 && (
                        <Typography sx={{ color: "text.secondary", px: 1 }}>Keine Elemente</Typography>
                    )}
                    {docsList.map(item => (
                        <ListItem
                            key={item.id}
                            divider
                            disablePadding
                        >
                            <Box sx={{ display: "grid", gridTemplateColumns: "1fr auto", alignItems: "center", width: "100%", px: 1 }}>
                                {/* Left column: two rows (bus on first row; place + status on second row, left-aligned) */}
                                <Box sx={{ pr: 1 }}>
                                    <Typography noWrap variant="body1">{String(item.bus ?? "")}</Typography>
                                    <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: "1fr 2fr 1fr", // place | info (twice wider) | status
                                            gap: 1,
                                            mt: 0.5,
                                            alignItems: "center",
                                        }}
                                    >
                                        {/* Place column */}
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
                                                {`place: ${String(item.place ?? "")}`}
                                            </Typography>
                                        </Box>

                                        {/* Info column (new) */}
                                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                                            <Typography
                                                sx={{
                                                    fontSize: 14,
                                                    color: "text.secondary",
                                                    whiteSpace: "normal",          // allow wrapping
                                                    wordBreak: "break-word",
                                                    overflowWrap: "break-word",
                                                    width: "100%",                 // fill the 1/3 column
                                                }}
                                            >
                                                {String(item.info ?? "")}
                                            </Typography>
                                        </Box>
                                        
                                        {/* Status column */}
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            {/* <Typography sx={{ fontSize: 14, color: "text.secondary" }}>status:</Typography> */}
                                            <Box
                                                aria-hidden
                                                sx={{
                                                    width: 20,
                                                    height: 20,
                                                    borderRadius: "50%",
                                                    bgcolor:
                                                        Number(item.status) === 1
                                                            ? "#fff59d"
                                                            : Number(item.status) === 2
                                                            ? "#f44336"
                                                            : "#fff",
                                                    border: "1px solid #000",
                                                    boxSizing: "border-box",
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                </Box>

                                {/* Right column: delete button, aligned to the right */}
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                                    <IconButton
                                        edge="end"
                                        aria-label="delete"
                                        onClick={() => handleDelete(item.id)}
                                        size="small"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            </Box>
                        </ListItem>
                    ))}
                </List>
                <Divider sx={{ my: 3 }} />
                <AdsterraAd />
            </Box>
        </>
    );
}
